/**
 * ClarificationCards - Interactive source/intent selection UI
 * 
 * Displays clickable cards when the system needs user clarification
 * about which data source or intent to use.
 */

import React from 'react';
import {
  MailRegular,
  CalendarRegular,
  PeopleTeamRegular,
  DocumentRegular,
  GlobeRegular,
  SearchRegular,
  LightbulbRegular,
  LibraryRegular,
  DataBarHorizontalRegular,
  AppsAddInRegular,
  QuestionCircleRegular,
} from '@fluentui/react-icons';
import styles from './ClarificationCards.module.css';

// Map backend icon names to FluentUI icons
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  'Mail': MailRegular,
  'Calendar': CalendarRegular,
  'TeamsLogo': PeopleTeamRegular,
  'OneDriveLogo': DocumentRegular,
  'SharepointLogo': DocumentRegular,
  'ExcelLogo': DataBarHorizontalRegular,
  'Globe': GlobeRegular,
  'Search': SearchRegular,
  'Lightbulb': LightbulbRegular,
  'Library': LibraryRegular,
  'Document': DocumentRegular,
  'DataConnectionLibrary': LibraryRegular,
  'SearchAndApps': AppsAddInRegular,
  'AnalyticsView': DataBarHorizontalRegular,
  'EditCreate': DocumentRegular,
  'Tag': QuestionCircleRegular,
  'PDF': DocumentRegular,
  'WordDocument': DocumentRegular,
};

export interface ClarificationOption {
  id: string;
  label: string;
  label_he: string;
  description?: string;
  description_he?: string;
  icon?: string;
  confidence?: number;
  metadata?: Record<string, any>;
}

export interface ClarificationRequest {
  type: string;
  prompt: string;
  prompt_he: string;
  options: ClarificationOption[];
  allow_custom?: boolean;
  default_option?: string;
  original_query?: string;
}

interface ClarificationCardsProps {
  clarification: ClarificationRequest;
  onSelect: (optionId: string, option: ClarificationOption) => void;
  language?: 'en' | 'he';
  disabled?: boolean;
}

export const ClarificationCards: React.FC<ClarificationCardsProps> = ({
  clarification,
  onSelect,
  language = 'en',
  disabled = false,
}) => {
  const isHebrew = language === 'he';
  
  const getLabel = (option: ClarificationOption): string => {
    return isHebrew && option.label_he ? option.label_he : option.label;
  };
  
  const getDescription = (option: ClarificationOption): string => {
    if (isHebrew && option.description_he) return option.description_he;
    return option.description || '';
  };
  
  const getIcon = (iconName?: string): React.ReactNode => {
    if (!iconName) return <QuestionCircleRegular className={styles.cardIcon} />;
    const IconComponent = ICON_MAP[iconName] || QuestionCircleRegular;
    return <IconComponent className={styles.cardIcon} />;
  };
  
  const formatConfidence = (confidence?: number): string => {
    if (confidence === undefined || confidence === 0) return '';
    return `${Math.round(confidence * 100)}%`;
  };
  
  const handleCardClick = (option: ClarificationOption) => {
    if (disabled) return;
    onSelect(option.id, option);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent, option: ClarificationOption) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(option);
    }
  };

  return (
    <div className={styles.clarificationContainer} dir={isHebrew ? 'rtl' : 'ltr'}>
      <div className={styles.cardsGrid}>
        {clarification.options.map((option) => (
          <div
            key={option.id}
            className={`${styles.card} ${disabled ? styles.cardDisabled : ''}`}
            onClick={() => handleCardClick(option)}
            onKeyDown={(e) => handleKeyDown(e, option)}
            tabIndex={disabled ? -1 : 0}
            role="button"
            aria-label={getLabel(option)}
            aria-disabled={disabled}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardIconWrapper}>
                {getIcon(option.icon)}
              </div>
              {option.confidence !== undefined && option.confidence > 0 && (
                <span className={styles.confidenceBadge}>
                  {formatConfidence(option.confidence)}
                </span>
              )}
            </div>
            
            <div className={styles.cardContent}>
              <h4 className={styles.cardTitle}>{getLabel(option)}</h4>
              {getDescription(option) && (
                <p className={styles.cardDescription}>{getDescription(option)}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {clarification.allow_custom && (
        <p className={styles.customHint}>
          {isHebrew 
            ? '...או הקלד לציין משהו אחר'
            : '...or type to specify something else'}
        </p>
      )}
    </div>
  );
};

export default ClarificationCards;
